import React, {useEffect, useState, useMemo} from 'react'
import {
    Box,
    Text,
    Image,
    VStack,
    HStack,
    Grid,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    AspectRatio,
    Link
} from '@chakra-ui/react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useCustomFetch from '../../hooks/useStoreDetails'
import {
    headerStyle,
    minitextStyle,
    pageContainerStyle,
    tableBodyStyle,
    tableHeaderStyle,
    textStyle,
    streetMapMobileHeaderStyle,
    streetMapMobileButtonStyle,
    addressHeaderStyle,
    addressContainerStyle,
    addressTextContainerStyle,
    phoneHeaderStyle,
    phoneContainerStyle,
    storeHoursHeaderStyle,
    storeHoursContainerStyle,
    tableContainerStyle,
    tableWrapperStyle,
    tableDayStyle,
    streetMapDesktopContainerStyle,
    streetMapDesktopHeaderStyle,
    streetMapDesktopButtonStyle,
    aboutContainerStyle,
    aboutHeaderStyle,
    storeServiceContainerStyle,
    storeServiceHeaderStyle
} from './index.style'
import GoogleMaps from '../../components/google-map'
import StoreBreadcrumbs from './partials/StoreBreadcrumbs'
import {useLocation} from 'react-router-dom'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useIntl} from 'react-intl'
import StoreSeo from './partials/StoreSeo'

const StoreDetails = () => {
    const {token} = useAccessToken()
    const location = useLocation()
    const navigate = useNavigation()
    const intl = useIntl()
    const searchParams = new URLSearchParams(location.search)
    const storeID = searchParams.get('StoreID')
    useEffect(() => {
        if (!storeID) {
            // Redirect to store locator landing page when StoreID is missing
            navigate('/storelocator')
        }
    }, [storeID, navigate])

    if (!storeID) return null

    const {response, loading, error} = useCustomFetch(token, storeID)

    const {
        image = '',
        c_image2 = '',
        c_image3 = '',
        name = '',
        address1 = '',
        city = '',
        stateCode = '',
        phone = '',
        c_firstHoursOnline = '',
        c_firstHoursRows = [],
        c_secondHoursOnline = '',
        c_secondHoursRows = [],
        c_thirdHoursOnline = '',
        c_thirdHoursRows = [],
        c_fourthHoursOnline = '',
        c_fourthHoursRows = [],
        c_fifthHoursOnline = '',
        c_fifthHoursRows = [],
        c_aboutStore = '',
        c_storeServices = '',
        latitude = 0.0,
        longitude = 0.0,
        c_pageTitle = '',
        c_pageDescription = '',
        postalCode = '',
        countryCode = '',
        c_firstHoursName = '',
        c_secondHoursName = '',
        c_thirdHoursName = '',
        c_fourthHoursName = '',
        c_fifthHoursName = ''
    } = response?.data?.[0] || {}

    const hoursData = []

    if (c_firstHoursOnline === true && c_firstHoursRows.length > 0) {
        hoursData.push({
            name: c_firstHoursName || 'REGULAR',
            rows: c_firstHoursRows
        })
    }
    if (c_secondHoursOnline === true && c_secondHoursRows.length > 0) {
        hoursData.push({
            name: c_secondHoursName || 'REGULAR',
            rows: c_secondHoursRows
        })
    }
    if (c_thirdHoursOnline === true && c_thirdHoursRows.length > 0) {
        hoursData.push({
            name: c_thirdHoursName || 'REGULAR',
            rows: c_thirdHoursRows
        })
    }
    if (c_fourthHoursOnline === true && c_fourthHoursRows.length > 0) {
        hoursData.push({
            name: c_fourthHoursName || 'REGULAR',
            rows: c_fourthHoursRows
        })
    }
    if (c_fifthHoursOnline === true && c_fifthHoursRows.length > 0) {
        hoursData.push({
            name: c_fifthHoursName || 'REGULAR',
            rows: c_fifthHoursRows
        })
    }

    const storeHoursTable = hoursData.length > 0

    const [selectedImage, setSelectedImage] = useState(image)

    useEffect(() => {
        if (image) {
            setSelectedImage(image)
        }
    }, [image])

    const allImages = [image, c_image2, c_image3].filter(Boolean)

    const parseStoreHours = (hoursArray) => {
        return hoursArray.map((hourString) => {
            const parts = hourString.split(' | ')
            return {
                day: parts[0] || '',
                open: parts[1] || '',
                close: parts[2] || ''
            }
        })
    }

    const handleThumbnailClick = (imageUrl) => {
        setSelectedImage(imageUrl)
    }

    const generateGoogleMapsUrl = () => {
        if (!latitude || !longitude || !name) return ''
        const encodedStoreName = encodeURIComponent(name)
        return `https://www.google.com/maps?q=${latitude},${longitude}+(${encodedStoreName})&ll=${latitude},${longitude}&z=17`
    }

    const handleGetDirections = () => {
        const mapsUrl = generateGoogleMapsUrl()
        if (mapsUrl) {
            window.open(mapsUrl, '_blank', 'noopener,noreferrer')
        }
    }

    const memoizedStoreList = useMemo(() => {
        return response?.data
            ? [
                  {
                      id: storeID,
                      name: name,
                      latitude: latitude,
                      longitude: longitude,
                      address1: address1,
                      city: city,
                      stateCode: stateCode,
                      countryCode: countryCode,
                      postalCode: postalCode
                  }
              ]
            : []
    }, [
        response?.data,
        storeID,
        name,
        latitude,
        longitude,
        address1,
        city,
        stateCode,
        countryCode,
        postalCode
    ])

    let openingHoursSpecifications = []

    if (hoursData.length) {
        openingHoursSpecifications = hoursData[0].rows.map((hourString) => {
            const parts = hourString.split(' | ')
            const daysOfWeek = {
                SUNDAY: 'Sunday',
                MONDAY: 'Monday',
                TUESDAY: 'Tuesday',
                WEDNESDAY: 'Wednesday',
                THURSDAY: 'Thursday',
                FRIDAY: 'Friday',
                SATURDAY: 'Saturday'
            }
            let day = daysOfWeek[parts[0]]
            let opens = parts[1].split(' ')
            let closes = parts[2].split(' ')

            if (opens[1] == 'AM' && opens[0].length > 1) {
                opens = `${opens[0]}:00`
            } else if (opens[1] == 'AM' && opens[0].length == 1) {
                opens = `0${opens[0]}:00`
            } else if (opens[0] == '12') {
                opens = `${opens[0]}:00`
            } else {
                opens = (parseInt(opens[0]) + 12).toString() + '00'
            }

            if (closes[1] == 'AM' && closes[0].length > 1) {
                closes = `${closes[0]}:00`
            } else if (closes[1] == 'AM' && closes[0].length == 1) {
                closes = `0${closes[0]}:00`
            } else if (closes[0] == '12') {
                closes = `${closes[0]}:00`
            } else {
                closes = (parseInt(closes[0]) + 12).toString() + ':00'
            }

            return {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: day || '',
                opens: opens || '',
                closes: closes || ''
            }
        })
    }

    const storeSeoData = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: name,
        image: image,
        '@id':
            typeof window !== 'undefined'
                ? window.location.href
                : 'https://www.cavenders.com' + location.pathname + location.search,
        url:
            typeof window !== 'undefined'
                ? window.location.href
                : 'https://www.cavenders.com' + location.pathname + location.search,
        telephone: phone,
        address: {
            '@type': 'PostalAddress',
            streetAddress: address1,
            addressLocality: city,
            addressRegion: stateCode,
            postalCode: postalCode,
            addressCountry: countryCode
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: latitude,
            longitude: longitude
        },
        openingHoursSpecification: openingHoursSpecifications,
        department: {
            '@type': 'ClothingStore',
            name: name,
            image: image,
            telephone: phone
        }
    }
    const seoProps = {name, city, stateCode, c_pageTitle, c_pageDescription, location, storeSeoData}

    return (
        !loading &&
        !error && (
            <>
                <StoreSeo {...seoProps}></StoreSeo>
                <Box bg="white" minH="100vh" sx={pageContainerStyle}>
                    <StoreBreadcrumbs address1={address1} city={city} state={stateCode} />
                    <Grid
                        templateColumns={{base: '1fr', xl: '1.13fr 1.82fr'}}
                        gap={{base: 4, xl: 0}}
                    >
                        {/* Left Column - Store Images and Info */}
                        <VStack align="stretch" spacing="0">
                            <Box>
                                <AspectRatio ratio={1.33} w="100%" mb="25px">
                                    <Image
                                        src={selectedImage}
                                        alt="Cavender's Western Outfitter"
                                        w="100%"
                                        h="100%"
                                        objectFit="cover"
                                        id="storeImage"
                                    />
                                </AspectRatio>
                                {allImages.length > 1 && (
                                    <HStack
                                        spacing={{base: '9', md: '90px', lg: '120px', xl: '50px'}}
                                        justify="space-between"
                                    >
                                        {allImages.map((imageUrl, index) => (
                                            <AspectRatio key={index} ratio={1.33} w="100%">
                                                <Image
                                                    src={imageUrl}
                                                    alt={`Cavender's Western Outfitter ${
                                                        !index
                                                            ? 'first'
                                                            : index === 1
                                                            ? 'second'
                                                            : 'third'
                                                    } image`}
                                                    w="100%"
                                                    h="100%"
                                                    objectFit="cover"
                                                    onClick={() => handleThumbnailClick(imageUrl)}
                                                    cursor="pointer"
                                                />
                                            </AspectRatio>
                                        ))}
                                    </HStack>
                                )}
                            </Box>
                            <Box>
                                {name !== '' && <Text sx={headerStyle}>{name}</Text>}
                                <Box
                                    display={{base: 'block', md: 'flex', xl: 'none'}}
                                    mb={{base: '30px'}}
                                >
                                    <Text sx={streetMapMobileHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'storedetails.streetmap',
                                            defaultMessage: 'STREET MAP:'
                                        })}
                                    </Text>
                                    <Box>
                                        <AspectRatio
                                            ratio={{base: 1.3, md: 0.66, lg: 0.88}}
                                            w="100%"
                                        >
                                            <GoogleMaps
                                                customLat={latitude}
                                                customLong={longitude}
                                                zoom={15}
                                                storeList={memoizedStoreList}
                                            />
                                        </AspectRatio>
                                        <Button
                                            sx={streetMapMobileButtonStyle}
                                            onClick={handleGetDirections}
                                        >
                                            {intl.formatMessage({
                                                id: 'storedetails.getdirections',
                                                defaultMessage: 'GET DIRECTIONS'
                                            })}
                                        </Button>
                                    </Box>
                                </Box>
                                <VStack align="stretch" spacing={{base: 4, xl: 5}}>
                                    <Box sx={addressContainerStyle}>
                                        <Text sx={addressHeaderStyle}>
                                            {intl.formatMessage({
                                                id: 'storedetails.address',
                                                defaultMessage: 'ADDRESS:'
                                            })}
                                        </Text>
                                        <Box sx={addressTextContainerStyle}>
                                            <Text sx={minitextStyle}>{address1}</Text>
                                            <Text
                                                sx={minitextStyle}
                                            >{`${city}, ${stateCode} ${postalCode} United States`}</Text>
                                        </Box>
                                    </Box>
                                    {phone && (
                                        <Box sx={phoneContainerStyle}>
                                            <Text sx={phoneHeaderStyle}>
                                                {intl.formatMessage({
                                                    id: 'storedetails.phone',
                                                    defaultMessage: 'PHONE:'
                                                })}
                                            </Text>
                                            <Link
                                                href={`tel:1${phone.replace(/\D/g, '')}`}
                                                sx={{
                                                    ...minitextStyle,
                                                    _hover: 'none',
                                                    display: 'block'
                                                }}
                                            >
                                                {phone}
                                            </Link>
                                        </Box>
                                    )}
                                    {storeHoursTable && (
                                        <Box sx={storeHoursContainerStyle}>
                                            <Text sx={storeHoursHeaderStyle}>
                                                {intl.formatMessage({
                                                    id: 'storedetails.storehours',
                                                    defaultMessage: 'STORE HOURS:'
                                                })}
                                            </Text>
                                            <VStack align="stretch" spacing="10px">
                                                {hoursData.map((hoursSection, tableIndex) => {
                                                    const storeHours = parseStoreHours(
                                                        hoursSection.rows
                                                    )
                                                    return (
                                                        <Box
                                                            key={tableIndex}
                                                            sx={tableWrapperStyle}
                                                        >
                                                            <TableContainer>
                                                                <Table size="sm" variant="unstyled">
                                                                    <Thead>
                                                                        <Tr>
                                                                            <Th
                                                                                sx={
                                                                                    tableHeaderStyle
                                                                                }
                                                                            >
                                                                                {hoursSection.name}
                                                                            </Th>
                                                                            <Th
                                                                                sx={
                                                                                    tableHeaderStyle
                                                                                }
                                                                            >
                                                                                OPEN
                                                                            </Th>
                                                                            <Th
                                                                                sx={
                                                                                    tableHeaderStyle
                                                                                }
                                                                            >
                                                                                CLOSE
                                                                            </Th>
                                                                        </Tr>
                                                                    </Thead>
                                                                    <Tbody>
                                                                        {storeHours.map(
                                                                            (hour, index) => (
                                                                                <Tr key={index}>
                                                                                    <Td
                                                                                        sx={
                                                                                            tableDayStyle
                                                                                        }
                                                                                    >
                                                                                        {hour.day}
                                                                                    </Td>
                                                                                    <Td
                                                                                        sx={
                                                                                            tableBodyStyle
                                                                                        }
                                                                                    >
                                                                                        {hour.open}
                                                                                    </Td>
                                                                                    <Td
                                                                                        sx={
                                                                                            tableBodyStyle
                                                                                        }
                                                                                    >
                                                                                        {hour.close}
                                                                                    </Td>
                                                                                </Tr>
                                                                            )
                                                                        )}
                                                                    </Tbody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Box>
                                                    )
                                                })}
                                            </VStack>
                                        </Box>
                                    )}
                                </VStack>
                            </Box>
                        </VStack>
                        {/* Right Column - Map and About */}
                        <VStack align="stretch" spacing={0}>
                            <Box sx={streetMapDesktopContainerStyle}>
                                <Text sx={streetMapDesktopHeaderStyle}>
                                    {intl.formatMessage({
                                        id: 'storedetails.streetmap',
                                        defaultMessage: 'STREET MAP:'
                                    })}
                                </Text>
                                <Box>
                                    <AspectRatio ratio={1.19} w="475px">
                                        <GoogleMaps
                                            customLat={latitude}
                                            customLong={longitude}
                                            zoom={15}
                                            storeList={memoizedStoreList}
                                        />
                                    </AspectRatio>
                                    <Button
                                        sx={streetMapDesktopButtonStyle}
                                        onClick={handleGetDirections}
                                    >
                                        {intl.formatMessage({
                                            id: 'storedetails.getdirections',
                                            defaultMessage: 'GET DIRECTIONS'
                                        })}
                                    </Button>
                                </Box>
                            </Box>
                            {c_aboutStore !== '' && (
                                <Box sx={aboutContainerStyle}>
                                    <Text sx={aboutHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'storedetails.about',
                                            defaultMessage: 'ABOUT:'
                                        })}
                                    </Text>
                                    <Box
                                        sx={textStyle}
                                        dangerouslySetInnerHTML={{__html: c_aboutStore}}
                                    />
                                </Box>
                            )}
                            {c_storeServices !== '' ? (
                                <Box sx={storeServiceContainerStyle}>
                                    <Text sx={storeServiceHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'storedetails.storeservice',
                                            defaultMessage: 'STORE SERVICE:'
                                        })}
                                    </Text>
                                    <Box
                                        sx={textStyle}
                                        dangerouslySetInnerHTML={{__html: c_storeServices}}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{mb: {md: '60px'}}} />
                            )}
                        </VStack>
                    </Grid>
                </Box>
            </>
        )
    )
}

export default StoreDetails
